import renderSiteWatermark from './site_watermark'
import { toDateValueSeries } from './series_utils'
import { cssVar, watchChartTheme } from '../../utils/chart_theme'

/**
 * Market Breadth Chart implementation for Highcharts
 *
 * @param {HTMLElement} container - The DOM element to render the chart in
 * @param {Object|Array} data - Chart data with series, title, etc.
 * @param {Object} options - Additional chart options
 */
export default function createMarketBreadthChart(container, data, options = {}) {
  // Format the chart data for Highcharts
  const seriesData = formatMarketBreadthData(data)

  // Get chart title from the options or generate a default
  const chartTitle = options.chartTitle || "Percentage of Stocks Above Moving Average"

  const theme = resolveBreadthChartTheme()

  // Create the chart configuration
  const chartConfig = {
    chart: {
      type: 'spline',
      zoomType: 'x',
      backgroundColor: 'transparent',
      style: {
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial'
      },
      events: {
        // Site watermark, bottom-right inside the green Oversold band —
        // clear of the breadth line and its label sits on the band's left.
        render: function() {
          renderSiteWatermark(this, (chart, box) => ({
            x: chart.plotLeft + chart.plotWidth - box.width - 16,
            y: chart.plotTop + chart.plotHeight - 16
          }))
        }
      }
    },
    title: {
      text: null
    },
    xAxis: {
      type: 'datetime',
      dateTimeLabelFormats: {
        day: '%b %e',
        week: '%b %e',
        month: '%b %Y'
      },
      title: {
        text: 'Date'
      },
      labels: {
        rotation: 0
      },
      crosshair: true
    },
    yAxis: {
      title: {
        text: 'Percentage (%)'
      },
      min: 0,
      max: 100,
      tickInterval: 10,
      plotBands: buildPlotBands(theme),
      plotLines: buildPlotLines(theme)
    },
    tooltip: {
      formatter: function() {
        return '<b>' + Highcharts.dateFormat('%b %e, %Y', this.x) + '</b><br/>' +
              chartTitle + ': <b>' + this.y.toFixed(2) + '%</b>';
      },
      crosshairs: true
    },
    legend: {
      enabled: true
    },
    plotOptions: {
      series: {
        states: {
          // The extreme-zone fills are separate series; without this,
          // hovering the main line dims them.
          inactive: {
            opacity: 1
          }
        }
      },
      spline: {
        marker: {
          enabled: false,
          radius: 4,
          fillColor: theme.line,
          lineWidth: 2,
          lineColor: theme.markerOutline,
          states: {
            hover: {
              enabled: true,
              radius: 5
            }
          }
        },
        lineWidth: 2.5,
        color: theme.line,
        states: {
          hover: {
            lineWidth: 3.5
          }
        },
        threshold: null,
        animation: {
          duration: 500
        },
        connectNulls: true,
        // Adjust smoothness based on data points
        tension: getDynamicTension(seriesData, options.days)
      },
      areaspline: {
        tension: getDynamicTension(seriesData, options.days)
      }
    },
    series: [{
      name: chartTitle,
      type: 'spline',
      data: seriesData,
      color: theme.line,
      zIndex: 2,
      enableMouseTracking: true,
      showInLegend: true,
      connectNulls: true,
      marker: {
        enabled: false,
        symbol: 'circle',
        states: {
          hover: {
            enabled: true,
            fillColor: theme.line,
            lineWidth: 2,
            lineColor: theme.markerOutline
          }
        }
      }
    }, ...buildExtremeZoneSeries(theme, seriesData)],
    responsive: {
      rules: [{
        condition: {
          maxWidth: 500
        },
        chartOptions: {
          legend: {
            enabled: false
          }
        }
      }]
    },
    credits: {
      enabled: false
    },
    ...options
  }

  const chart = Highcharts.chart(container, chartConfig)

  watchChartTheme(chart, () => {
    const next = resolveBreadthChartTheme()
    chart.update({
      yAxis: {
        plotBands: buildPlotBands(next),
        plotLines: buildPlotLines(next)
      },
      plotOptions: {
        spline: {
          color: next.line,
          marker: { fillColor: next.line, lineColor: next.markerOutline }
        }
      }
    }, false)
    chart.series[0].update({
      color: next.line,
      marker: { states: { hover: { fillColor: next.line, lineColor: next.markerOutline } } }
    }, false)
    chart.redraw(false)
  })

  return chart
}

// Resolve theme-aware colours from the active Bootstrap palette so the
// chart is legible on both --bs-body-bg variants.
function resolveBreadthChartTheme() {
  return {
    line: cssVar('--bs-body-color', '#212529'),
    markerOutline: cssVar('--bs-body-bg', '#ffffff'),
    label: cssVar('--bs-secondary-color', '#6c757d'),
    oversoldFill: 'rgba(104, 187, 138, 0.15)',  // same palette as gics_breadth strong zone
    overboughtFill: 'rgba(218, 104, 104, 0.15)', // same palette as gics_breadth weak zone
    oversoldExtremeFill: 'rgba(104, 187, 138, 0.45)',
    overboughtExtremeFill: 'rgba(218, 104, 104, 0.45)'
  }
}

// Shadow series that fill the gap between the breadth line and the 20/80
// thresholds only while the line is beyond them. Highcharts clips an
// areaspline fill at its threshold, so fillColor paints the area above it
// and negativeFillColor the area below — making one side transparent
// yields a fill for just the extreme excursions, cut off exactly at the
// crossing points.
function buildExtremeZoneSeries(theme, seriesData) {
  const base = {
    type: 'areaspline',
    data: seriesData,
    lineWidth: 0,
    color: 'rgba(0, 0, 0, 0)',
    marker: { enabled: false },
    enableMouseTracking: false,
    showInLegend: false,
    connectNulls: true,
    zIndex: 1
  }

  return [
    {
      ...base,
      name: 'Oversold Zone',
      threshold: 20,
      fillColor: 'rgba(0, 0, 0, 0)',
      negativeFillColor: theme.oversoldExtremeFill
    },
    {
      ...base,
      name: 'Overbought Zone',
      threshold: 80,
      fillColor: theme.overboughtExtremeFill,
      negativeFillColor: 'rgba(0, 0, 0, 0)'
    }
  ]
}

function buildPlotBands(theme) {
  return [
    { // Oversold region (0-20%)
      from: 0,
      to: 20,
      color: theme.oversoldFill,
      label: {
        text: 'Oversold',
        style: { color: theme.label, fontWeight: 'bold', fontSize: '12px' },
        align: 'left',
        verticalAlign: 'bottom',
        x: 10,
        y: -12 // baseline 12px above the chart's bottom edge
      }
    },
    { // Overbought region (80-100%)
      from: 80,
      to: 100,
      color: theme.overboughtFill,
      label: {
        text: 'Overbought',
        style: { color: theme.label, fontWeight: 'bold', fontSize: '12px' },
        align: 'left',
        verticalAlign: 'top',
        x: 10,
        y: 20 // baseline 20px below the chart's top edge (~12px visual gap)
      }
    }
  ]
}

function buildPlotLines(theme) {
  return [20, 80].map(value => ({
    value,
    width: 1,
    color: theme.label,
    dashStyle: 'ShortDash'
  }))
}

/**
 * Format market breadth data for the chart
 * 
 * @param {Array|Object} data - Raw chart data
 * @returns {Array} - Formatted series data for Highcharts
 */
function formatMarketBreadthData(data) {
  // Handle empty or invalid data
  if (!data) {
    console.error('Invalid or empty data for market breadth chart')
    return []
  }
  
  // If data is already an array of date-value pairs, use the shared
  // formatter (also used by the linked index price chart, so both charts map
  // dates to identical timestamps).
  if (Array.isArray(data) && data.length > 0 && Array.isArray(data[0]) && data[0].length === 2) {
    return toDateValueSeries(data)
  }
  
  // Handle array of objects format (e.g., [{date: "2024-01-01", value: 42}])
  if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'object') {
    return data.map(item => {
      // Extract date and value
      const dateStr = item.date || item[0]
      const value = item.value || item[1]
      
      // Parse the date string to a timestamp
      const date = new Date(dateStr).getTime()
      
      // Make sure we have valid data
      const numValue = parseFloat(value)
      
      if (!isNaN(date) && !isNaN(numValue)) {
        return [date, numValue]
      }
      return null
    }).filter(point => point !== null).sort((a, b) => a[0] - b[0])
  }
  
  // Handle raw Highcharts series data format
  if (data.series && Array.isArray(data.series) && data.series[0] && Array.isArray(data.series[0].data)) {
    return data.series[0].data
  }
  
  // Return empty array if no valid format found
  console.error('Invalid data format for market breadth chart', data)
  return []
}

/**
 * Calculate appropriate tension/smoothness based on number of data points and timeframe
 * 
 * @param {Array} data - The series data points
 * @param {Number} days - The number of days in the chart timeframe
 * @returns {Number} - The tension value (0-1) for the spline curve
 */
function getDynamicTension(data, days) {
  // Default to 30 days if not specified
  const daysValue = parseInt(days) || 30
  
  // Number of data points affects how much smoothing is appropriate
  const pointCount = data ? data.length : 0
  
  // For short timeframes (30 days), use moderate smoothing
  if (daysValue <= 30) {
    return 0.33  // More curvature for short-term trends
  }
  // For medium timeframes (30-90 days), reduce smoothing
  else if (daysValue <= 90) {
    return 0.25
  }
  // For 6-month timeframes
  else if (daysValue <= 180) {
    return 0.2
  }
  // For long timeframes (365 days+), use minimal smoothing to preserve trends
  else {
    return 0.15
  }
}